import api from './api';

export type TenantStatus = 'active' | 'suspended' | 'deactivated' | 'deleted';

export interface TenantRow {
  _id: string;
  companyName: string;
  slug: string;
  domain?: string;
  subdomain?: string;
  country: string;
  currency: string;
  timezone: string;
  defaultLanguage: string;
  languages: string[];
  subscriptionPlan: string;
  subscriptionExpiry?: string;
  status?: TenantStatus;
  isActive: boolean;
  modules: { key: string; enabled: boolean }[];
  /** Primary client admin contact (from list API). */
  adminEmail?: string;
  adminPhone?: string;
  branding?: {
    logo?: string;
    favicon?: string;
    primaryColor?: string;
    secondaryColor?: string;
    fontFamily?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface TenantFormPayload {
  companyName: string;
  slug: string;
  domain?: string;
  subdomain?: string;
  country: string;
  currency: string;
  timezone: string;
  defaultLanguage: string;
  languages: string[];
  subscriptionPlan: string;
  subscriptionExpiry?: string;
  primaryColor: string;
  secondaryColor: string;
  logo?: string;
  favicon?: string;
  fontFamily?: string;
  enabledModules: string[];
  adminUser?: {
    email: string;
    password: string;
    firstName: string;
    lastName?: string;
    phone?: string;
    /** Dial code for the admin phone UI (e.g. +91); composed into E.164 before API submit. */
    phoneCountryCode?: string;
  };
}

export interface ListTenantsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: TenantStatus | 'all';
}

export function slugifyCompany(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export async function listTenants(params: ListTenantsParams = {}) {
  const res = await api.get('/tenants', { params });
  const body = res.data as {
    data?: TenantRow[];
    total?: number;
    meta?: { total?: number; page?: number; limit?: number };
  };
  const rows = Array.isArray(body?.data) ? body.data : [];
  const total = Number(body?.meta?.total ?? body?.total ?? rows.length);
  return {
    data: rows,
    total: Number.isFinite(total) ? total : rows.length,
    page: Number(body?.meta?.page ?? params.page ?? 1),
    limit: Number(body?.meta?.limit ?? params.limit ?? 20),
  };
}

export async function checkTenantDomainAvailability(params: {
  domain?: string;
  subdomain?: string;
  excludeId?: string;
}) {
  const res = await api.get('/tenants/check-domain', {
    params: {
      domain: params.domain || undefined,
      subdomain: params.subdomain || undefined,
      excludeId: params.excludeId || undefined,
    },
  });
  return res.data.data as {
    domainAvailable: boolean;
    subdomainAvailable: boolean;
    domainError?: string;
    subdomainError?: string;
  };
}

export async function getTenant(id: string) {
  const res = await api.get(`/tenants/${id}`);
  return res.data.data as TenantRow;
}

export interface TenantAdminDetails {
  id: string;
  email: string;
  phone?: string;
  firstName: string;
  lastName?: string;
  isActive?: boolean;
}

export async function getTenantAdmin(id: string) {
  const res = await api.get(`/tenants/${id}/admin`);
  return (res.data.data ?? null) as TenantAdminDetails | null;
}

export async function upsertTenantAdmin(
  id: string,
  payload: {
    email: string;
    password?: string;
    firstName: string;
    lastName?: string;
    phone?: string;
  },
) {
  const res = await api.put(`/tenants/${id}/admin`, payload);
  return res.data.data as TenantAdminDetails;
}

export async function createTenant(payload: TenantFormPayload) {
  const { logo, favicon, fontFamily, primaryColor, secondaryColor, enabledModules, adminUser, ...rest } = payload;
  const res = await api.post('/tenants', {
    ...rest,
    enabledModules: Array.isArray(enabledModules) ? [...enabledModules] : [],
    adminUser: adminUser?.email
      ? {
          email: adminUser.email,
          password: adminUser.password,
          firstName: adminUser.firstName,
          lastName: adminUser.lastName,
          phone: adminUser.phone || undefined,
        }
      : undefined,
    branding: { logo, favicon, fontFamily, primaryColor, secondaryColor },
  });
  return res.data as { data: { tenant: TenantRow } };
}

export async function updateTenant(id: string, payload: Partial<TenantFormPayload>) {
  const { logo, favicon, fontFamily, primaryColor, secondaryColor, enabledModules, adminUser, ...rest } = payload;
  void enabledModules;
  void adminUser;
  const res = await api.put(`/tenants/${id}`, {
    ...rest,
    branding: { logo, favicon, fontFamily, primaryColor, secondaryColor },
  });
  return res.data;
}

export async function updateTenantModules(id: string, modules: { key: string; enabled: boolean }[]) {
  const res = await api.put(`/tenants/${id}/modules`, { modules });
  return res.data;
}

export async function updateTenantStatus(id: string, status: TenantStatus) {
  const res = await api.patch(`/tenants/${id}/status`, { status });
  return res.data;
}

export async function deleteTenant(id: string) {
  const res = await api.delete(`/tenants/${id}`);
  return res.data;
}

export const SUBSCRIPTION_PLANS = [
  { value: 'free', label: 'Free' },
  { value: 'starter', label: 'Starter' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise', label: 'Enterprise' },
];

export const TENANT_STATUS_META: Record<TenantStatus, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  active: { label: 'Active', color: 'success' },
  suspended: { label: 'Suspended', color: 'warning' },
  deactivated: { label: 'Deactivated', color: 'default' },
  deleted: { label: 'Deleted', color: 'error' },
};
